import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Table2, ExternalLink, Download, Upload, Network, Copy } from 'lucide-react';
import ResultsGrid from '../DbResultsGrid';
import ContextMenu from '../ui/ContextMenu';
import { EmptyState } from '../dbGridCells';
import { cycleSort } from '../dbGridSort';
import { cellKey, EMPTY_PENDING_EDITS, EMPTY_PENDING_ROWS } from '../dbGridModel';
import { openDbManagerTab } from '../../store/tabsStore';
import { patchDbOverviewTab } from '../../store/dbOverviewTabs';
import { quoteIdentFor } from '../../extensions/dbEngines';
import { loadCounts, loadSection } from './dbOverviewCatalog';
import { sectionsFor, OPENABLE_SECTIONS, TABLE_OWNED_SECTIONS } from './dbOverviewSections';
import { buildOverviewGrid, recordsToTsv } from './dbOverviewGridModel';
import { tableKey } from './dbDiagramModel';
import DbOverviewSectionList from './DbOverviewSectionList';

/**
 * The Overview half: section list on the left, the section's objects on the
 * right in THE results grid (same sorting, column resize, virtualisation,
 * selection) — read-only, client-side sorted and filtered.
 */
export default function DbOverviewObjects({ tab, ctx, nonce, onOpenTable, onShowInDiagram }) {
  const { t, i18n } = useTranslation();
  const sections = sectionsFor(tab.engine);
  const section = sections.includes(tab.overviewSection) ? tab.overviewSection : sections[0];
  const [counts, setCounts] = useState({});
  const [state, setState] = useState({ loading: true, error: null, records: null, section });
  const [filter, setFilter] = useState('');
  const [sortBySection, setSortBySection] = useState({});
  const [widthsBySection, setWidthsBySection] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);
  const [menu, setMenu] = useState(null);
  const rootRef = useRef(null);
  const filterRef = useRef(null);

  // Postgres over every schema shows the schema column; one schema does not need it.
  const showSchema = tab.engine === 'postgres' && !ctx.scope;

  useEffect(() => {
    let alive = true;
    loadCounts(ctx).then((c) => alive && setCounts(c || {}));
    return () => {
      alive = false;
    };
  }, [ctx, nonce]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null, section }));
    loadSection(ctx, section)
      .then((records) => alive && setState({ loading: false, error: null, records, section }))
      .catch((e) => alive && setState({ loading: false, error: e?.message || String(e), records: null, section }));
    return () => {
      alive = false;
    };
  }, [ctx, section, nonce]);

  // A new section or a new filter is a new list: indexes into the old one mean nothing.
  useEffect(() => {
    setSelected(new Set());
    setAnchor(null);
  }, [section, filter, nonce]);

  const labelFor = useCallback(
    (key) => (key === 'name' ? t(`dbOverview.nameOf.${section}`) : t(`dbOverview.col.${key}`)),
    [t, section]
  );
  // The grid sorts by its column LABEL; the model by key.
  const sortLevels = useMemo(() => sortBySection[section] || [], [sortBySection, section]);
  const grid = useMemo(
    () =>
      buildOverviewGrid({
        engine: tab.engine,
        sectionId: section,
        records: state.section === section ? state.records : null,
        filter,
        sortLevels,
        labelFor,
        locale: i18n.language,
        showSchema,
      }),
    [tab.engine, section, state, filter, sortLevels, labelFor, i18n.language, showSchema]
  );
  const labelToKey = useMemo(() => new Map(grid.keys.map((k, i) => [grid.result.columns[i], k])), [grid]);
  const gridSort = useMemo(
    () => sortLevels.map((l) => ({ col: grid.result.columns[grid.keys.indexOf(l.col)], dir: l.dir })).filter((l) => l.col),
    [sortLevels, grid]
  );

  const pickSection = (id) => {
    patchDbOverviewTab(tab.id, { overviewSection: id });
    setFilter('');
  };

  const onSortColumn = (label, additive) => {
    const key = labelToKey.get(label);
    if (!key) return;
    setSortBySection((prev) => ({ ...prev, [section]: cycleSort(prev[section] || [], key, { additive }) }));
  };

  // Widths are per section and keyed by label, which is what the grid hands back.
  const columnWidths = widthsBySection[section];
  const onColumnResize = (col, width) => {
    setWidthsBySection((prev) => {
      const cur = { ...(prev[section] || {}) };
      if (col === null) Object.assign(cur, width || {});
      else if (width == null) delete cur[col];
      else cur[col] = width;
      return { ...prev, [section]: cur };
    });
  };

  const recordAt = (i) => grid.records[i] || null;

  /** What double-click / Enter does on a row of this section. */
  const activate = (rec) => {
    if (!rec) return;
    if (OPENABLE_SECTIONS.has(section)) onOpenTable(rec, { isView: section === 'views' });
    else if (TABLE_OWNED_SECTIONS.has(section) && rec.table) onOpenTable({ name: rec.table, schema: rec.schema });
  };

  const selectRow = (index, e) => {
    if (e?.shiftKey && anchor != null) {
      const [a, b] = anchor < index ? [anchor, index] : [index, anchor];
      setSelected(new Set(Array.from({ length: b - a + 1 }, (_, k) => a + k)));
      return;
    }
    if (e && (e.ctrlKey || e.metaKey)) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
      });
    } else {
      setSelected(new Set([index]));
    }
    setAnchor(index);
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const copySelected = () => {
    const idx = [...selected].sort((a, b) => a - b);
    if (!idx.length) return;
    copyText(recordsToTsv(grid.keys, grid.result.columns, idx.map((i) => grid.result.rows[i])));
  };

  const qualified = (rec) =>
    tab.engine === 'postgres' && rec.schema
      ? `${quoteIdentFor('postgres', rec.schema)}.${quoteIdentFor('postgres', rec.name)}`
      : tab.engine === 'mysql' && tab.dbName
        ? `${quoteIdentFor('mysql', tab.dbName)}.${quoteIdentFor('mysql', rec.name)}`
        : quoteIdentFor(tab.engine, rec.name);

  const onKeyDown = (e) => {
    if (e.target === filterRef.current) {
      if (e.key === 'ArrowDown' && grid.records.length) {
        e.preventDefault();
        selectRow(0);
        rootRef.current?.querySelector('.dbo-grid')?.focus();
      }
      if (e.key === 'Escape' && filter) {
        e.preventDefault();
        setFilter('');
      }
      return;
    }
    const current = anchor ?? -1;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(0, Math.min(grid.records.length - 1, current + (e.key === 'ArrowDown' ? 1 : -1)));
      if (grid.records.length) selectRow(next, e.shiftKey ? e : undefined);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(recordAt(current));
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      copySelected();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setSelected(new Set(grid.records.map((_, i) => i)));
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
      e.preventDefault();
      filterRef.current?.focus();
      filterRef.current?.select();
    }
  };

  const openMenu = (e, index) => {
    e.preventDefault();
    if (!selected.has(index)) selectRow(index);
    setMenu({ x: e.clientX, y: e.clientY, rec: recordAt(index) });
  };

  const menuItems = (rec) => {
    if (!rec) return [];
    const isTable = section === 'tables';
    const tableRef = TABLE_OWNED_SECTIONS.has(section) && rec.table ? { name: rec.table, schema: rec.schema } : null;
    const dbName = tab.engine === 'postgres' ? rec.schema || tab.dbName || 'public' : tab.dbName || 'main';
    const pending = (action) => () =>
      openDbManagerTab({
        extensionId: tab.extensionId,
        connId: tab.connId,
        engine: tab.engine,
        dbName,
        tableName: rec.name,
        label: rec.name,
        pendingAction: action,
      });
    return [
      OPENABLE_SECTIONS.has(section) && { icon: <Table2 size={14} />, label: t('dbOverview.menu.openData'), onClick: () => activate(rec) },
      OPENABLE_SECTIONS.has(section) && {
        icon: <ExternalLink size={14} />,
        label: t('dbOverview.menu.openNewTab'),
        onClick: () => onOpenTable(rec, { newTab: true, isView: section === 'views' }),
      },
      tableRef && { icon: <Table2 size={14} />, label: t('dbOverview.menu.openTable', { table: rec.table }), onClick: () => onOpenTable(tableRef) },
      isTable && { separator: true },
      isTable && { icon: <Download size={14} />, label: t('dbOverview.menu.export'), onClick: pending('export') },
      isTable && { icon: <Upload size={14} />, label: t('dbOverview.menu.import'), onClick: pending('import') },
      isTable && {
        icon: <Network size={14} />,
        label: t('dbOverview.menu.showInDiagram'),
        onClick: () => onShowInDiagram(tableKey(tab.engine === 'postgres' ? rec.schema : tab.engine === 'mysql' ? tab.dbName : 'main', rec.name)),
      },
      { separator: true },
      { icon: <Copy size={14} />, label: t('dbOverview.menu.copyName'), onClick: () => copyText(rec.name) },
      tab.engine !== 'sqlite' &&
        (isTable || section === 'views') && {
          icon: <Copy size={14} />,
          label: t('dbOverview.menu.copyQualified'),
          onClick: () => copyText(qualified(rec)),
        },
      selected.size > 1 && { icon: <Copy size={14} />, label: t('dbOverview.menu.copyRows', { count: selected.size }), onClick: copySelected },
    ]
      .filter(Boolean)
      // A section without the open/export entries would start on a divider.
      .filter((item, i, all) => !item.separator || (i > 0 && !all[i - 1].separator));
  };

  const records = state.section === section ? state.records : null;
  const emptyList = !state.loading && !state.error && records && records.length === 0;
  const noMatches = !state.loading && !state.error && records && records.length > 0 && grid.records.length === 0;
  const selectedKeys = useMemo(() => {
    // The grid's cell selection draws the row; a row selection here is the row.
    const s = new Set();
    selected.forEach((i) => grid.keys.forEach((_, j) => s.add(cellKey('r', i, j))));
    return s;
  }, [selected, grid.keys]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard for the list (Enter/arrows/Ctrl+C) lives on its container
    <div className="dbo-body" ref={rootRef} onKeyDown={onKeyDown}>
      <DbOverviewSectionList sections={sections} active={section} counts={counts} onPick={pickSection} />
      <div className="dbo-objects">
        <div className="dbo-objects-toolbar">
          <div className="dbo-filter">
            <Search size={13} className="dbo-filter-icon" aria-hidden />
            <input
              ref={filterRef}
              type="text"
              className="dbo-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('dbOverview.filterPlaceholder', { what: t(`dbOverview.section.${section}`) })}
              aria-label={t('dbOverview.filterLabel')}
              spellCheck={false}
            />
            {filter && (
              <button type="button" className="dbo-filter-clear" onClick={() => setFilter('')} aria-label={t('dbOverview.clearFilter')}>
                <X size={12} />
              </button>
            )}
          </div>
          <span className="dbo-objects-count" role="status">
            {records &&
              (filter
                ? t('dbOverview.countFiltered', { shown: grid.records.length, total: records.length })
                : t('dbOverview.count', { count: records.length }))}
          </span>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the grid takes focus for keyboard navigation */}
        <div className="dbo-grid" tabIndex={0} aria-label={t(`dbOverview.section.${section}`)}>
          {emptyList ? (
            <EmptyState>{t(`dbOverview.empty.${section}`)}</EmptyState>
          ) : noMatches ? (
            <EmptyState>{t('dbOverview.noMatches', { filter })}</EmptyState>
          ) : (
            <ResultsGrid
              loading={state.loading}
              error={state.error}
              result={records ? grid.result : null}
              canAddRow={false}
              editable={false}
              pendingRows={EMPTY_PENDING_ROWS}
              pendingEdits={EMPTY_PENDING_EDITS}
              editingCell={null}
              editingValue=""
              sortable
              sortState={gridSort}
              onSortColumn={onSortColumn}
              rowSelectable
              selectedRows={selected}
              onToggleRowSelect={(index, e) => selectRow(index, e)}
              selectedCells={selectedKeys}
              onCellSelect={(e, kind, index) => {
                if (kind === 'r') selectRow(index, e);
              }}
              onCellOpen={(index) => activate(recordAt(index))}
              onCellContextMenu={(e, index) => openMenu(e, index)}
              columnWidths={columnWidths}
              onColumnResize={onColumnResize}
              cursorKey={anchor != null ? cellKey('r', anchor, 0) : null}
              scrollRestoreKey={`${tab.id}:${section}`}
            />
          )}
        </div>
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.rec)} onClose={() => setMenu(null)} />}
    </div>
  );
}
